import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Button } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useSelector, useDispatch } from 'react-redux';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import NavigationIcon from '@material-ui/icons/Navigation';
import mapboxgl from 'mapbox-gl';
import moment from 'moment';
import { setShowMobilePreview, selectUI } from '../../store/slices/uiSlice';
import { selectCurrentRoute } from '../../store/slices/routesSlice';
import { selectVehicles } from '../../store/slices/vehiclesSlice';
import { FUEL_TYPES } from '../Sidebar/VehicleDriverForm';
import { t } from '../../i18n';
import { getCurrency } from '../../utils/currencies';

const TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

const STOP_COLORS = {
  break_45: '#f59e0b',
  break_split_15: '#3b82f6',
  break_split_30: '#3b82f6',
  daily_rest: '#22c55e',
};

const STOP_LABEL_KEYS = {
  break_45: 'stopBreak45',
  break_split_15: 'stopBreakSplit15',
  break_split_30: 'stopBreakSplit30',
  daily_rest: 'stopDailyRest',
};

const useStyles = makeStyles(() => ({
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(10,15,30,0.72)',
    backdropFilter: 'blur(10px)',
    zIndex: 1300,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  phone: {
    position: 'relative',
    width: 290,
    height: 590,
    background: 'linear-gradient(145deg, #2c2c2e, #1c1c1e)',
    borderRadius: 44,
    boxShadow: '0 30px 80px rgba(0,0,0,0.7), inset 0 0 0 1px rgba(255,255,255,0.08)',
    flexShrink: 0,
    userSelect: 'none',
  },
  volUp: {
    position: 'absolute',
    left: -3,
    top: 110,
    width: 3,
    height: 32,
    background: 'linear-gradient(90deg, #1a1a1c, #2c2c2e)',
    borderRadius: '2px 0 0 2px',
    boxShadow: '-1px 0 4px rgba(0,0,0,0.4)',
  },
  volDown: {
    position: 'absolute',
    left: -3,
    top: 152,
    width: 3,
    height: 32,
    background: 'linear-gradient(90deg, #1a1a1c, #2c2c2e)',
    borderRadius: '2px 0 0 2px',
    boxShadow: '-1px 0 4px rgba(0,0,0,0.4)',
  },
  power: {
    position: 'absolute',
    right: -3,
    top: 130,
    width: 3,
    height: 50,
    background: 'linear-gradient(270deg, #1a1a1c, #2c2c2e)',
    borderRadius: '0 2px 2px 0',
    boxShadow: '1px 0 4px rgba(0,0,0,0.4)',
  },
  screen: {
    position: 'absolute',
    inset: '10px',
    borderRadius: 34,
    overflow: 'hidden',
    background: '#f8fafc',
    display: 'flex',
    flexDirection: 'column',
  },
  statusBar: {
    height: 28,
    background: '#111827',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 16px',
    flexShrink: 0,
    position: 'relative',
  },
  punchHole: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 12,
    height: 12,
    borderRadius: '50%',
    background: '#000',
  },
  appBar: {
    height: 48,
    background: 'linear-gradient(90deg, #1d3f58 0%, #1a8a6a 60%, #22b878 100%)',
    display: 'flex',
    alignItems: 'center',
    padding: '0 12px',
    gap: 8,
    flexShrink: 0,
  },
  scrollContent: {
    flex: 1,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    '&::-webkit-scrollbar': { width: 3 },
    '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.12)', borderRadius: 4 },
  },
  mapContainer: {
    height: 200,
    width: '100%',
    flexShrink: 0,
  },
  routeCard: {
    background: '#ffffff',
    borderRadius: '16px 16px 0 0',
    marginTop: -20,
    zIndex: 10,
    padding: '12px 14px 8px',
    boxShadow: '0 -4px 12px rgba(0,0,0,0.08)',
    flexShrink: 0,
  },
  dot: {
    width: 7,
    height: 7,
    borderRadius: '50%',
    flexShrink: 0,
  },
  statsRow: {
    display: 'flex',
    gap: 5,
    flexWrap: 'wrap',
    marginTop: 8,
  },
  statChip: {
    padding: '3px 7px',
    borderRadius: 20,
    fontSize: '0.58rem',
    fontWeight: 700,
  },
  stopsSection: {
    padding: '8px 14px 4px',
    background: '#ffffff',
    flexShrink: 0,
    borderTop: '1px solid rgba(0,0,0,0.05)',
  },
  stopRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    paddingBottom: 7,
  },
  stopCircle: {
    width: 20,
    height: 20,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.55rem',
    fontWeight: 700,
    flexShrink: 0,
    marginTop: 1,
  },
  ctaSection: {
    padding: '10px 14px 6px',
    background: '#ffffff',
    flexShrink: 0,
  },
  ctaBtn: {
    width: '100%',
    background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
    color: '#ffffff',
    fontWeight: 700,
    fontSize: '0.75rem',
    padding: '9px 0',
    borderRadius: 12,
    textTransform: 'none',
    '&:hover': { background: 'linear-gradient(135deg, #4ade80 0%, #22c55e 100%)' },
  },
  homeIndicator: {
    height: 22,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#ffffff',
    flexShrink: 0,
  },
}));

function LiveClock() {
  const [time, setTime] = useState(() => {
    const now = new Date();
    return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  });
  useEffect(() => {
    const id = setInterval(() => {
      const now = new Date();
      setTime(`${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`);
    }, 10000);
    return () => clearInterval(id);
  }, []);
  return <Typography style={{ fontSize: '0.65rem', color: '#e2e8f0', fontWeight: 600 }}>{time}</Typography>;
}

// Accepts an optional `routeOverride` prop so callers can render the phone
// preview for any route (e.g. from the OT list's "Ver no telemóvel") without
// having to load the route into Redux's currentRoute slot. When omitted, the
// component falls back to the planner's currentRoute — its original behaviour.
// An `onClose` callback lets the caller dismiss the overlay without relying on
// the global `showMobilePreview` flag.
function PhonePreview({ routeOverride, onClose } = {}) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { lang } = useSelector(selectUI);
  const reduxRoute = useSelector(selectCurrentRoute);
  const route = routeOverride || reduxRoute;
  const vehicles = useSelector(selectVehicles);
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);

  const cr = route.computedRoute;
  const stops = route.stops || [];
  const vehicle = vehicles.find(v => v.id === route.vehicleId);
  const currency = getCurrency(route.routeOptions?.currency || 'EUR');
  const currSym = currency.symbol;

  const distanceKm = cr ? (cr.distance / 1000) : null;
  const durationMin = cr ? Math.round(cr.duration / 60) : 0;
  const durationStr = cr ? `${Math.floor(durationMin / 60)}h ${durationMin % 60}min` : null;

  // Same logic as RouteDetails
  const fuelType = route.fuelType || 'diesel';
  const fuelDef = FUEL_TYPES.find(f => f.value === fuelType) || FUEL_TYPES[0];
  const consumption = route.fuelConsumption ?? vehicle?.fuelPer100km ?? fuelDef.consumptionDefault;
  const pricePerUnit = route.fuelPrice ?? fuelDef.priceDefault;
  const totalFuelCostEUR = distanceKm ? (distanceKm / 100) * consumption * pricePerUnit : 0;
  const totalFuelCost = totalFuelCostEUR * currency.rate;
  const dp = currency.rate >= 100 ? 0 : 2;
  const costStr = totalFuelCost > 0
    ? `${currSym}${totalFuelCost.toLocaleString('pt-PT', { minimumFractionDigits: dp, maximumFractionDigits: dp })}`
    : null;

  const totalRestMin = stops.reduce((s, st) => s + (st.duration || 0), 0);
  const departureStr = route.departureTime ? moment(route.departureTime).format('DD/MM HH:mm') : null;
  const arrivalStr = route.departureTime && cr
    ? moment(route.departureTime).add(durationMin + totalRestMin, 'minutes').format('DD/MM HH:mm')
    : null;

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current || !cr || !TOKEN) return;
    mapboxgl.accessToken = TOKEN;
    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      interactive: false,
      attributionControl: false,
    });
    map.on('load', () => {
      const coords = cr.geometry.coordinates;
      map.addSource('phone-route', {
        type: 'geojson',
        data: { type: 'Feature', geometry: { type: 'LineString', coordinates: coords } },
      });
      map.addLayer({
        id: 'phone-route-line',
        type: 'line',
        source: 'phone-route',
        paint: { 'line-color': '#3b82f6', 'line-width': 3, 'line-opacity': 0.9 },
      });
      if (route.origin?.coordinates) {
        new mapboxgl.Marker({ color: '#22c55e' })
          .setLngLat(route.origin.coordinates)
          .addTo(map);
      }
      if (route.destination?.coordinates) {
        new mapboxgl.Marker({ color: '#ef4444' })
          .setLngLat(route.destination.coordinates)
          .addTo(map);
      }
      const lngs = coords.map(c => c[0]);
      const lats = coords.map(c => c[1]);
      map.fitBounds(
        [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]],
        { padding: 24, duration: 0, maxZoom: 14 }
      );
    });
    mapRef.current = map;
    return () => {
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
    };
  }, []); // eslint-disable-line

  function handleOverlayClick(e) {
    if (e.target === e.currentTarget) {
      if (typeof onClose === 'function') onClose();
      else dispatch(setShowMobilePreview(false));
    }
  }

  return (
    <Box className={classes.overlay} onClick={handleOverlayClick}>
      <Box className={classes.phone}>
        {/* Side buttons */}
        <Box className={classes.volUp} />
        <Box className={classes.volDown} />
        <Box className={classes.power} />

        {/* Screen */}
        <Box className={classes.screen}>

          {/* Status bar */}
          <Box className={classes.statusBar}>
            <LiveClock />
            <Box className={classes.punchHole} />
            <Typography style={{ fontSize: '0.6rem', color: '#94a3b8', letterSpacing: '0.03em' }}>
              ▐▐▐ ▓ 🔋
            </Typography>
          </Box>

          {/* App bar */}
          <Box className={classes.appBar}>
            <Box style={{
              width: 24, height: 24, borderRadius: 6, flexShrink: 0,
              background: 'linear-gradient(135deg, #22c55e, #16a34a)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Typography style={{ fontSize: '0.6rem', color: '#fff', fontWeight: 900 }}>E</Typography>
            </Box>
            <Typography style={{ fontSize: '0.78rem', fontWeight: 700, color: '#fff', flex: 1 }}>
              EcooGPS
            </Typography>
            <Box style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <CheckCircleIcon style={{ fontSize: 11, color: '#4ade80' }} />
              <Typography style={{ fontSize: '0.55rem', color: '#4ade80', fontWeight: 600 }}>
                {t(lang, 'routeReceived')}
              </Typography>
            </Box>
          </Box>

          {/* Scrollable content */}
          <Box className={classes.scrollContent}>

            {/* Map */}
            <Box className={classes.mapContainer}>
              <div ref={mapContainerRef} style={{ width: '100%', height: '100%' }} />
            </Box>

            {/* Route card (overlaps map) */}
            <Box className={classes.routeCard}>
              <Typography style={{ fontSize: '0.62rem', color: '#94a3b8', marginBottom: 3, fontStyle: 'italic' }}>
                {t(lang, 'sentRoute')}
              </Typography>
              <Typography style={{ fontSize: '0.8rem', fontWeight: 700, color: '#0f172a', marginBottom: 5 }}>
                {route.name || `${route.origin?.name?.split(',')[0]} → ${route.destination?.name?.split(',')[0]}`}
              </Typography>
              {route.cargoRef && (
                <Typography style={{ fontSize: '0.62rem', color: '#6366f1', fontWeight: 600, marginBottom: 5 }}>
                  {route.cargoRef}
                </Typography>
              )}

              {/* Origin → Destination */}
              <Box style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Box className={classes.dot} style={{ background: '#22c55e' }} />
                  <Typography style={{ fontSize: '0.67rem', color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {route.origin?.name || '—'}
                  </Typography>
                </Box>
                <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Box className={classes.dot} style={{ background: '#ef4444' }} />
                  <Typography style={{ fontSize: '0.67rem', color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {route.destination?.name || '—'}
                  </Typography>
                </Box>
              </Box>

              {/* Stats chips */}
              <Box className={classes.statsRow}>
                {distanceKm && (
                  <Box className={classes.statChip}
                    style={{ background: 'rgba(59,130,246,0.08)', color: '#3b82f6', border: '1px solid rgba(59,130,246,0.2)' }}>
                    {distanceKm?.toFixed(1)} km
                  </Box>
                )}
                {durationStr && (
                  <Box className={classes.statChip}
                    style={{ background: 'rgba(99,102,241,0.08)', color: '#6366f1', border: '1px solid rgba(99,102,241,0.2)' }}>
                    {durationStr}
                  </Box>
                )}
                {costStr && (
                  <Box className={classes.statChip}
                    style={{ background: 'rgba(34,197,94,0.08)', color: '#16a34a', border: '1px solid rgba(34,197,94,0.2)' }}>
                    ≈{costStr}
                  </Box>
                )}
                {stops.length > 0 && (
                  <Box className={classes.statChip}
                    style={{ background: 'rgba(245,158,11,0.08)', color: '#d97706', border: '1px solid rgba(245,158,11,0.2)' }}>
                    {stops.length} {stops.length === 1 ? t(lang, 'stopSingular') : t(lang, 'stopPlural')}
                  </Box>
                )}
              </Box>
            </Box>

            {/* Departure / Arrival */}
            {(departureStr || arrivalStr) && (
              <Box style={{ padding: '8px 14px', background: '#fff', borderTop: '1px solid rgba(0,0,0,0.05)' }}>
                {departureStr && (
                  <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography style={{ fontSize: '0.6rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      {t(lang, 'departureTime')}
                    </Typography>
                    <Typography style={{ fontSize: '0.7rem', color: '#0f172a', fontWeight: 600 }}>
                      {departureStr}
                    </Typography>
                  </Box>
                )}
                {arrivalStr && (
                  <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
                    <Typography style={{ fontSize: '0.6rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      {t(lang, 'arrivalTime')}
                    </Typography>
                    <Typography style={{ fontSize: '0.7rem', color: '#0f172a', fontWeight: 600 }}>
                      {arrivalStr}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {/* Stops timeline */}
            {stops.length > 0 && (
              <Box className={classes.stopsSection}>
                <Typography style={{ fontSize: '0.58rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                  {t(lang, 'restStops')}
                </Typography>
                {stops.map((stop, idx) => {
                  const color = STOP_COLORS[stop.type] || '#f59e0b';
                  const labelKey = STOP_LABEL_KEYS[stop.type] || 'stopBreak45';
                  return (
                    <Box key={idx} className={classes.stopRow}>
                      <Box className={classes.stopCircle} style={{ background: `${color}22`, color }}>
                        {idx + 1}
                      </Box>
                      <Box style={{ flex: 1 }}>
                        <Typography style={{ fontSize: '0.67rem', fontWeight: 600, color }}>
                          {t(lang, labelKey)}
                        </Typography>
                        <Typography style={{ fontSize: '0.6rem', color: '#64748b' }}>
                          {stop.km} km · {stop.duration}min{stop.arrivalTime ? ` · ${stop.arrivalTime}` : ''}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}

            {/* CTA */}
            <Box className={classes.ctaSection}>
              <Button
                className={classes.ctaBtn}
                startIcon={<NavigationIcon style={{ fontSize: 16 }} />}
              >
                {t(lang, 'startNavigation')}
              </Button>
            </Box>

            {/* Home indicator */}
            <Box className={classes.homeIndicator}>
              <Box style={{ width: 100, height: 4, borderRadius: 2, background: 'rgba(0,0,0,0.15)' }} />
            </Box>

          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default PhonePreview;
